import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  PropsWithChildren,
} from 'react';

import { createAppTheme, Mode, MODE_THEME, MODES, ThemeName } from '@/theme';

const STORAGE_KEY = 'hauser.mode';
const DEFAULT_MODE: Mode = 'day';

interface AppThemeContextProps {
  // Day or Night: what the page you read is made of (paper or slate). Tools are slate in both
  mode: Mode;
  setMode: (mode: Mode) => void;
  // the shared brand-family theme the mode renders with (natural or dark)
  themeName: ThemeName;
}

export const AppThemeContext = createContext<AppThemeContextProps | null>(null);

export const useAppThemeContext = () => {
  const context = useContext(AppThemeContext);

  if (!context) throw new Error('useAppThemeContext context must be used inside AppThemeProvider');

  return context;
};

// The choice is a per-browser convenience; storage can be unavailable (private windows), so every access is guarded
const readStoredMode = (): Mode => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return MODES.includes(stored as Mode) ? (stored as Mode) : DEFAULT_MODE;
  } catch {
    return DEFAULT_MODE;
  }
};

export const AppThemeProvider = ({ children }: PropsWithChildren) => {
  const [mode, setModeState] = useState<Mode>(readStoredMode);
  const themeName = MODE_THEME[mode];

  const setMode = useCallback((next: Mode) => {
    setModeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // not persisted; the choice still applies for this visit
    }
  }, []);

  // tokens.css and hauser.css key every surface, ink and light off <html data-theme>
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = themeName;
    root.dataset.mode = mode;
    root.style.colorScheme = themeName === 'dark' ? 'dark' : 'light';
  }, [mode, themeName]);

  const theme = useMemo(() => createAppTheme(themeName), [themeName]);

  const memoizedValue = useMemo(() => ({ mode, setMode, themeName }), [mode, setMode, themeName]);

  return (
    <AppThemeContext.Provider value={memoizedValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </AppThemeContext.Provider>
  );
};
